import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, RefreshCw, CheckCircle } from 'lucide-react';
import { parseISO, addDays } from 'date-fns';
import { toast } from 'sonner';

// Use relative path for production compatibility
const API = '/api';

const AvailabilityCalendar = ({ showRefresh = false, token = null }) => {
  const [bookedDates, setBookedDates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshingAirbnb, setIsRefreshingAirbnb] = useState(false);
  const [isRefreshingVrbo, setIsRefreshingVrbo] = useState(false);
  const [airbnbLastSynced, setAirbnbLastSynced] = useState(null);
  const [vrboLastSynced, setVrboLastSynced] = useState(null);
  const [totalBookings, setTotalBookings] = useState(0);

  const fetchAvailability = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/availability`);
      if (response.data.success) {
        const dates = [];
        response.data.booked_dates.forEach(booking => {
          const start = parseISO(booking.start);
          const end = parseISO(booking.end);
          
          // Include start date up to and including end date
          // (Backend already adjusted end to be last blocked night, not checkout day)
          let current = new Date(start);
          const endDate = new Date(end);
          
          while (current <= endDate) {
            dates.push(new Date(current));
            current = addDays(current, 1);
          }
        });
        setBookedDates(dates);
        setTotalBookings(response.data.total_bookings || dates.length);
        setAirbnbLastSynced(response.data.airbnb_last_synced);
        setVrboLastSynced(response.data.vrbo_last_synced);
      }
    } catch (error) {
      console.error('Error fetching availability:', error);
      toast.error('Failed to load availability calendar');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  const handleRefreshAirbnb = async () => {
    if (!token) return;
    
    setIsRefreshingAirbnb(true);
    try {
      const [username, password] = token.split(':');
      const response = await axios.post(`${API}/availability/refresh`, {}, {
        auth: { username, password }
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        await fetchAvailability();
      }
    } catch (error) {
      console.error('Error refreshing Airbnb availability:', error);
      toast.error('Failed to refresh Airbnb calendar');
    } finally {
      setIsRefreshingAirbnb(false);
    }
  };

  const handleRefreshVrbo = async () => {
    if (!token) return;
    
    setIsRefreshingVrbo(true);
    try {
      const [username, password] = token.split(':');
      const response = await axios.post(`${API}/availability/refresh/vrbo`, {}, {
        auth: { username, password }
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        await fetchAvailability();
      }
    } catch (error) {
      console.error('Error refreshing Vrbo availability:', error);
      toast.error('Failed to refresh Vrbo calendar');
    } finally {
      setIsRefreshingVrbo(false);
    }
  };

  return (
    <Card className="border-border" data-testid="availability-calendar-widget">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="font-playfair text-2xl font-bold text-center" data-testid="calendar-title">
              Real-Time Availability
            </h3>
            <p className="text-center text-muted-foreground text-sm" data-testid="calendar-subtitle">
              Live updates from Airbnb & Vrbo
            </p>
          </div>
          {showRefresh && token && (
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={handleRefreshAirbnb}
                disabled={isRefreshingAirbnb || isRefreshingVrbo}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
                data-testid="refresh-airbnb-button"
              >
                {isRefreshingAirbnb ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Refreshing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4" />
                    Airbnb iCal
                  </>
                )}
              </Button>
              <Button
                onClick={handleRefreshVrbo}
                disabled={isRefreshingAirbnb || isRefreshingVrbo}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
                data-testid="refresh-vrbo-button"
              >
                {isRefreshingVrbo ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Refreshing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4" />
                    Vrbo iCal
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex flex-col items-center">
        {isLoading ? (
          <div className="flex items-center justify-center py-8" data-testid="calendar-loading">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          <>
            <Calendar
              mode="single"
              disabled={(date) => {
                return bookedDates.some(
                  bookedDate =>
                    bookedDate.getDate() === date.getDate() &&
                    bookedDate.getMonth() === date.getMonth() &&
                    bookedDate.getFullYear() === date.getFullYear()
                );
              }}
              modifiers={{
                booked: bookedDates
              }}
              modifiersStyles={{
                booked: {
                  textDecoration: 'line-through',
                  opacity: 0.4,
                  backgroundColor: '#e5e5e5'
                }
              }}
              className="rounded-none"
              data-testid="availability-calendar"
            />
            <div className="mt-4 text-center" data-testid="calendar-legend">
              <div className="flex items-center justify-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <span className="inline-block w-6 h-6 bg-white border border-border rounded-sm"></span>
                  <span>Available</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-block w-6 h-6 bg-gray-300 rounded-sm opacity-60"></span>
                  <span>Booked</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Combined from Airbnb & Vrbo - {bookedDates.length} nights currently booked
              </p>
              <div className="mt-2 space-y-1">
                {airbnbLastSynced && (
                  <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                    <CheckCircle className="h-3 w-3 text-green-500" />
                    Airbnb synced: {new Date(airbnbLastSynced).toLocaleString()}
                  </p>
                )}
                {vrboLastSynced && (
                  <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                    <CheckCircle className="h-3 w-3 text-blue-500" />
                    Vrbo synced: {new Date(vrboLastSynced).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default AvailabilityCalendar;